import React from 'react';
import { DayTotals } from '../../types';
import { formatCurrency } from '../../tipCalculationsNew';
import './TipTotalsRow.scss';

interface TipTotalsRowProps {
  dates: string[];
  dayTotals: DayTotals; // User-declared values (editable)
  originalDayTotals: DayTotals; // System default values (from API)
  onUpdateDayTotal: (date: string, value: number) => void;
  getDayTotal: (date: string) => number; // Actual distributed amount
  isViewMode?: boolean;
}

const TipTotalsRow: React.FC<TipTotalsRowProps> = ({
  dates,
  dayTotals,
  originalDayTotals,
  onUpdateDayTotal,
  getDayTotal,
  isViewMode = false,
}) => {
  // User-declared grand total
  const userGrandTotal = dates.reduce((sum, date) => sum + (dayTotals[date] || 0), 0);
  // System default grand total
  const systemGrandTotal = dates.reduce((sum, date) => sum + (originalDayTotals[date] || 0), 0);
  // Distributed total
  const distributedTotal = dates.reduce((sum, date) => sum + getDayTotal(date), 0);
  // Total difference (user declared - system default)
  const totalDifference = userGrandTotal - systemGrandTotal;

  // Calculate difference for a specific date
  const getDifference = (date: string): number => {
    return (dayTotals[date] || 0) - (originalDayTotals[date] || 0);
  };

  const handleInputChange = (date: string, value: string) => {
    // Remove any non-digit characters and parse as integer
    const cleanValue = value.replace(/\D/g, '');
    const numValue = cleanValue === '' ? 0 : parseInt(cleanValue, 10);
    if (!isNaN(numValue)) {
      onUpdateDayTotal(date, numValue);
    }
  };

  return (
    <>
      {/* User-declared totals row (editable) */}
      <div className="table-row table-totals-row">
        <div className="table-cell col-user">
          <span className="totals-label">Propinas a Distribuir</span>
          <span className="totals-sublabel">(Declarado)</span>
        </div>
        <div className="table-cell col-rate">-</div>
        {dates.map(date => (
          <div key={`total-${date}`} className="table-cell col-day">
            {isViewMode ? (
              <span className="total-display">{formatCurrency(dayTotals[date] || 0)}</span>
            ) : (
              <div className="total-input-wrapper">
                <input
                  type="text"
                  value={formatCurrency(dayTotals[date] || 0)}
                  onChange={e => handleInputChange(date, e.target.value)}
                  className="total-input"
                  inputMode="numeric"
                />
              </div>
            )}
          </div>
        ))}
        <div className="table-cell col-total">
          <span className="total-amount">{formatCurrency(userGrandTotal)}</span>
        </div>
      </div>

      {/* System default row */}
      <div className="table-row table-system-row">
        <div className="table-cell col-user">
          <span className="system-label">Sistema</span>
        </div>
        <div className="table-cell col-rate">-</div>
        {dates.map(date => (
          <div key={`system-${date}`} className="table-cell col-day">
            <span className="system-amount">{formatCurrency(originalDayTotals[date] || 0)}</span>
          </div>
        ))}
        <div className="table-cell col-total">
          <span className="system-total">{formatCurrency(systemGrandTotal)}</span>
        </div>
      </div>

      {/* Difference row */}
      <div className="table-row table-difference-row">
        <div className="table-cell col-user">
          <span className="difference-label">Diferencia</span>
        </div>
        <div className="table-cell col-rate">-</div>
        {dates.map(date => {
          const diff = getDifference(date);
          return (
            <div key={`diff-${date}`} className="table-cell col-day">
              <span className={`difference-amount ${diff > 0 ? 'positive' : diff < 0 ? 'negative' : 'zero'}`}>
                {diff > 0 ? '+' : ''}{formatCurrency(diff)}
              </span>
            </div>
          );
        })}
        <div className="table-cell col-total">
          <span className={`difference-total ${totalDifference > 0 ? 'positive' : totalDifference < 0 ? 'negative' : 'zero'}`}>
            {totalDifference > 0 ? '+' : ''}{formatCurrency(totalDifference)}
          </span>
        </div>
      </div>

      {/* Distributed row */}
      <div className="table-row table-distributed-row">
        <div className="table-cell col-user">
          <span className="distributed-label">Distribuido (Real)</span>
        </div>
        <div className="table-cell col-rate">-</div>
        {dates.map(date => (
          <div key={`distributed-${date}`} className="table-cell col-day">
            <span className="distributed-amount">{formatCurrency(getDayTotal(date))}</span>
          </div>
        ))}
        <div className="table-cell col-total">
          <span className="distributed-total">{formatCurrency(distributedTotal)}</span>
        </div>
      </div>
    </>
  );
};

export default TipTotalsRow;
